import { FC, MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	CheckOutline,
	ChevronRightOutline,
	ColorModeValue,
	ColorSwatchOutline,
	DesktopComputerOutline,
	HStack,
	MoonOutline,
	rem,
	SunOutline,
	Text,
} from '@bonusly/design-system';
import { useCustomTheme } from '@bonusly/ds-theme';

import { useSeasonalTheme } from '../../../components/seasonal/seasonal-theme-context';
import { SeasonalToggle } from '../../../components/seasonal/seasonal-toggle';
import { useUserColorModeWithSync } from '../../../hooks/use-user-color-mode-with-sync';
import { UserNavMenu } from './user-nav-menu';

type ThemeSubmenuProps = {
	isExpanded: boolean;
};

export const ThemeSubmenu: FC<ThemeSubmenuProps> = ({ isExpanded }) => {
	const { t } = useTranslation('navigation');
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const { userPreference, setColorMode, canChangeColorMode } = useUserColorModeWithSync();
	const { activePreset, clearTheme } = useCustomTheme();

	const handleColorModeChange = useCallback(
		(mode: ColorModeValue) => {
			if (mode !== 'dark' && activePreset?.category === 'full') {
				clearTheme();
			}
			setColorMode(mode);
		},
		[activePreset, clearTheme, setColorMode]
	);

	if (!canChangeColorMode) {
		return null;
	}

	const modes: { value: ColorModeValue; icon: React.ReactElement; label: string }[] = [
		{ value: 'system', icon: <DesktopComputerOutline size="md" />, label: t('user_menu.theme_system', { defaultValue: 'System' }) },
		{ value: 'light', icon: <SunOutline size="md" />, label: t('user_menu.theme_light', { defaultValue: 'Light' }) },
		{ value: 'dark', icon: <MoonOutline size="md" />, label: t('user_menu.theme_dark', { defaultValue: 'Dark' }) },
	];

	// Active styling when seasonal theme is enabled (Image 2)
	const themeColor = isSeasonalThemeEnabled ? 'primary.fg' : 'text.default';
	const hoverStyle = isSeasonalThemeEnabled ? { bg: 'primary.bgAlpha', color: 'primary.fgStrong' } : { bg: 'secondary.bgAlpha' };

	return (
		<UserNavMenu
			buttonIcon={
				<Box color={themeColor}>
					<ColorSwatchOutline size="md" />
				</Box>
			}
			buttonContents={
				<HStack justifyContent="space-between" alignItems="center" width="100%">
					<HStack spacing={2} alignItems="center">
						<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap" color={themeColor}>
							{t('user_menu.theme', { defaultValue: 'Theme' })}
						</Text>
						{isSeasonalThemeEnabled && (
							// Pink/coral dot when seasonal theme is enabled
							<Box h={rem(6)} w={rem(6)} bg="#E5548F" borderRadius="full" />
						)}
					</HStack>
					<ChevronRightOutline size="sm" color={isSeasonalThemeEnabled ? 'primary.fg' : 'text.soft'} />
				</HStack>
			}
			isExpanded={isExpanded}
			isSubmenu
			placement="right-start"
			offset={[0, 0]}
		>
			{/* SEASONAL TOGGLE */}
			<SeasonalToggle isExpanded={true} />

			{/* SEPARATOR */}
			<Box height="1px" bg={isSeasonalThemeEnabled ? 'primary.bgAlpha' : 'border.soft'} mx={rem(10)} my={rem(4)} />

			{/* COLOR MODES */}
			{modes.map((mode) => {
				const isSelected = userPreference === mode.value;
				const isChecked = isSelected && !isSeasonalThemeEnabled;

				return (
					<Box
						key={mode.value}
						as="button"
						display="block"
						w="100%"
						py={rem(8)}
						px={rem(10)}
						borderRadius="md"
						bg="none"
						color={isSeasonalThemeEnabled ? 'text.disabled' : isSelected ? 'primary.fg' : 'text.default'}
						cursor={isSeasonalThemeEnabled ? 'not-allowed' : 'pointer'}
						opacity={isSeasonalThemeEnabled ? 0.4 : 1}
						_hover={hoverStyle}
						_active={hoverStyle}
						onClick={(e: MouseEvent<HTMLButtonElement>) => {
							if (isSeasonalThemeEnabled) {
								e.stopPropagation();
								return;
							}
							handleColorModeChange(mode.value);
						}}
						data-testid={`theme-mode-${mode.value}`}
					>
						<HStack justifyContent="space-between" alignItems="center" width="100%">
							<HStack spacing={3} alignItems="center">
								<Box display="flex" alignItems="center" color={isSeasonalThemeEnabled ? 'text.disabled' : isSelected ? 'primary.fg' : 'text.soft'}>
									{mode.icon}
								</Box>
								<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
									{mode.label}
								</Text>
							</HStack>
							{isChecked && <CheckOutline size="sm" color="primary.fg" />}
						</HStack>
					</Box>
				);
			})}
		</UserNavMenu>
	);
};
